// Tokens · Tone
//
// Shared closed-enum tone vocabulary. Used by Box (surfaces) and TextValue
// (text colors). Each tone has a consistent "intent" that both primitives
// interpret in their own register:
//
//   tone            Box (surface)                  TextValue (text)
//   ─────────────── ────────────────────────────── ─────────────────────────
//   neutral         no chrome                      inherit (no color)
//   primary         white surface                  TEXT.primary
//   secondary       surfaceSoft                    TEXT.secondary
//   muted           surfaceSoft                    TEXT.muted
//   faint           bg                             TEXT.faint
//   revenue         green softBg + green text      DOMAIN.revenue
//   cost            red softBg   + red text        DOMAIN.cost
//   correction      amber softBg + amber text      DOMAIN.correction
//   green/red/blue/amber/teal/purple/grey/orange/lime/pink/cyan/stone:
//                   TONE[t].softBg + TONE[t].text  TONE[t].text

import {
  DOMAIN,
  SURFACE,
  TEXT,
  TONE,
  type ToneName,
} from "@ds/tokens/palette";

export type Tone =
  | "neutral"
  | "primary"
  | "secondary"
  | "muted"
  | "faint"
  | "revenue"
  | "cost"
  | "correction"
  | ToneName;

// ─── Box (surface) resolver ───────────────────────────────────────────────

export interface ResolvedBoxTone {
  background?: string;
  color?:      string;
}

export function resolveBoxTone(tone: Tone): ResolvedBoxTone {
  switch (tone) {
    case "neutral":    return {};
    case "primary":    return { background: SURFACE.surface };
    case "secondary":  return { background: SURFACE.surfaceSoft };
    case "muted":      return { background: SURFACE.surfaceSoft };
    case "faint":      return { background: SURFACE.bg };
    case "revenue":    return { background: TONE.green.softBg, color: DOMAIN.revenue };
    case "cost":       return { background: TONE.red.softBg,   color: DOMAIN.cost };
    case "correction": return { background: TONE.amber.softBg, color: DOMAIN.correction };
    default: {
      const ramp = TONE[tone];
      return { background: ramp.softBg, color: ramp.text };
    }
  }
}

// ─── TextValue (text color) resolver ──────────────────────────────────────

export function resolveTextTone(tone: Tone): string | undefined {
  switch (tone) {
    case "neutral":    return undefined;
    case "primary":    return TEXT.primary;
    case "secondary":  return TEXT.secondary;
    case "muted":      return TEXT.muted;
    case "faint":      return TEXT.faint;
    case "revenue":    return DOMAIN.revenue;
    case "cost":       return DOMAIN.cost;
    case "correction": return DOMAIN.correction;
    default:           return TONE[tone].text;
  }
}
